import i18n from '@/i18n'
import { usersService } from '@/helpers/services/users.service'
import { filtersHelper } from '@/utils'
import {
  LISTING_STATE,
  listingMutations,
  listAction,
  detailAction,
  writeAction,
  updatedMsg,
} from '@/state/listing.store'

export const USERS_MODULE = 'users'

// ACTIONS — the names the pages call. Renaming one is how you get
// "this.getAllUser is not a function", which Vue only reports when the button
// is pressed, not at build time.
export const GET_ALL_USER = 'getAllUser'
export const GET_USER = 'getUsers'
export const CREATE_USER = 'createUsers'
export const UPDATE_USER = 'updateUsers'
export const UPDATE_USER_STATUS = 'updateUsersStatus'
export const UPDATE_USER_LOGIN_ACCESS = 'updateUsersLoginAccess'
export const UNLOCK_USER = 'unlockUsers'

export const state = LISTING_STATE()

/**
 * One API row -> one table row.
 *
 * camelCase throughout, because that is what the templates read. `id` rather
 * than `uuid` for the same reason: the listing's row actions all take `row.id`
 * and the shape is shared with every other listing in the app.
 */
function mapUserRow(item) {
  return {
    id: item.uuid,
    name: item.name,
    email: item.email,
    tenantIds: item.tenant_ids || [],
    // Printed instead of an empty cell. The server sends the names alongside
    // the ids so the UI does not have to join them against the brand list, and
    // sends is_platform_staff so it does not infer that from an empty array
    // and get it backwards.
    tenantNames: item.tenant_names || [],
    isPlatformStaff: item.is_platform_staff,
    isActive: item.is_active,
    isLocked: item.is_locked,
    isAdmin: item.is_admin,
    isAllowLogin: item.is_allow_login,
    // Null until they have followed a first-time-login link. "Invited" vs
    // "active" is read off this, and it is the first thing to check when
    // somebody says they cannot sign in.
    isVerified: !!item.email_verified_date_time,
    roles: (item.roles || []).map((r) => ({ id: r.uuid, name: r.name })),
    roleId: (item.roles || [])[0] ? item.roles[0].uuid : '',
    createdAt: filtersHelper.getDateFormat(item.created_at),
    updatedAt: filtersHelper.getDateFormat(item.updated_at),
  }
}

export const actions = {
  [GET_ALL_USER]: listAction((filter) => usersService.getAllUsers(filter)),

  [GET_USER]: detailAction(({ id }) => usersService.getUser(id)),

  [CREATE_USER]: writeAction(
    ({ data }) => usersService.createUser(data),
    (payload, result) =>
      // The account exists either way; only the e-mail may have failed. Saying
      // "created" and nothing else is how somebody waits for an invitation
      // that is not coming.
      result && result.invite_sent === false
        ? i18n.t('user.createdButNotEmailed')
        : i18n.t('user.createdAndEmailed'),
    GET_ALL_USER
  ),

  [UPDATE_USER]: writeAction(
    ({ id, data }) => usersService.updateUser(id, data),
    updatedMsg('module.user'),
    GET_ALL_USER
  ),

  [UPDATE_USER_STATUS]: writeAction(
    ({ id, isActive }) => usersService.updateUserStatus(id, isActive),
    () => i18n.t('label.updateX', [i18n.t('label.status')]),
    GET_ALL_USER
  ),

  [UPDATE_USER_LOGIN_ACCESS]: writeAction(
    ({ id, isAllowLogin }) => usersService.updateUserLoginAccess(id, isAllowLogin),
    () => i18n.t('label.updateX', [i18n.t('user.allowLogin')]),
    GET_ALL_USER
  ),

  [UNLOCK_USER]: writeAction(
    ({ id }) => usersService.unlockUser(id),
    () => i18n.t('label.unlockAccount'),
    GET_ALL_USER
  ),

}

export const mutations = listingMutations(mapUserRow)
